/**
 * AuthObservabilityPage - Auth health monitoring and drift detection
 *
 * DRR-317: Surfaces auth state observability for admin users.
 * Shows capability validation status, drift detection, and login simulation.
 *
 * Route: /admin/auth-observability
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Group,
  Badge,
  Text,
  ThemeIcon,
  Card,
  Stack,
  Table,
  Button,
  Alert,
  Select,
  Paper,
  Progress,
  Grid,
  Code,
  Loader,
  Accordion,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconRefresh,
  IconCamera,
  IconUser,
} from '@tabler/icons-react';

import { useAuth } from '@/auth/AuthContext';
import {
  useAuthObservability,
  useSimulateLogin,
  type AuthFailure,
  type DriftItem,
  type CapabilityCheck,
} from '@/hooks/useAuthObservability';

export function AuthObservabilityPage() {
  const { tenant } = useAuth();
  const { health, drift, personas, snapshot, baseline, refetchAll } = useAuthObservability();
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);
  const { simulation, loading: simLoading } = useSimulateLogin(selectedPersona);

  const handleGenerateSnapshot = async () => {
    await snapshot.generateSnapshot();
    refetchAll();
  };

  const handleSetBaseline = async () => {
    if (snapshot.snapshot?.snapshotId) {
      await baseline.setBaseline(snapshot.snapshot.snapshotId);
      refetchAll();
    }
  };

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon
            size="lg"
            variant="light"
            color={health.healthStatus?.passed ? 'green' : 'red'}
          >
            <IconShieldCheck size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>Auth Observability</Title>
            <Text size="sm" c="dimmed">
              DRR-317: Role capability validation, drift detection, login simulation
            </Text>
          </div>
        </Group>
        <Group>
          {tenant && (
            <Badge variant="light" color="gray">
              {tenant.name}
            </Badge>
          )}
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={refetchAll}
            loading={health.loading || drift.loading}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      <Grid gutter="md">
        {/* Health Status Card */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="md">
              <Text fw={600}>Capability Health</Text>
              {health.loading ? (
                <Loader size="sm" />
              ) : health.healthStatus?.passed ? (
                <Badge color="green" leftSection={<IconCheck size={12} />}>
                  Passed
                </Badge>
              ) : (
                <Badge color="red" leftSection={<IconX size={12} />}>
                  Failed
                </Badge>
              )}
            </Group>

            {health.healthStatus && (
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Total Assertions
                  </Text>
                  <Text fw={500}>{health.healthStatus.totalAssertions}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Failures
                  </Text>
                  <Text fw={500} c={health.healthStatus.failures.length > 0 ? 'red' : 'green'}>
                    {health.healthStatus.failures.length}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Last Check
                  </Text>
                  <Text size="xs" c="dimmed">
                    {new Date(health.healthStatus.checkedAt).toLocaleString()}
                  </Text>
                </Group>

                {health.healthStatus.failures.length > 0 && (
                  <Alert
                    color="red"
                    title="Capability Failures"
                    icon={<IconAlertTriangle size={16} />}
                  >
                    <Stack gap="xs">
                      {health.healthStatus.failures.slice(0, 5).map((f: AuthFailure, i: number) => (
                        <Text key={i} size="xs">
                          <Code>{f.roleId}</Code>: {f.message}
                        </Text>
                      ))}
                      {health.healthStatus.failures.length > 5 && (
                        <Text size="xs" c="dimmed">
                          +{health.healthStatus.failures.length - 5} more...
                        </Text>
                      )}
                    </Stack>
                  </Alert>
                )}
              </Stack>
            )}
          </Card>
        </Grid.Col>

        {/* Drift Status Card */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="md">
              <Text fw={600}>Drift Detection</Text>
              {drift.loading ? (
                <Loader size="sm" />
              ) : drift.driftStatus?.hasDrift ? (
                <Badge color="orange" leftSection={<IconAlertTriangle size={12} />}>
                  Drift Detected
                </Badge>
              ) : (
                <Badge color="green" leftSection={<IconCheck size={12} />}>
                  No Drift
                </Badge>
              )}
            </Group>

            {drift.driftStatus && (
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Scopes Removed
                  </Text>
                  <Text fw={500} c={drift.driftStatus.removed.length > 0 ? 'red' : 'green'}>
                    {drift.driftStatus.removed.length}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Scopes Added
                  </Text>
                  <Text fw={500} c={drift.driftStatus.added.length > 0 ? 'orange' : 'green'}>
                    {drift.driftStatus.added.length}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Files Changed
                  </Text>
                  <Text fw={500}>{drift.driftStatus.changed.length}</Text>
                </Group>

                {drift.driftStatus.removed.length > 0 && (
                  <Alert color="red" title="Removed Scopes" icon={<IconX size={16} />}>
                    <Stack gap="xs">
                      {drift.driftStatus.removed.slice(0, 3).map((d: DriftItem, i: number) => (
                        <Text key={i} size="xs">
                          <Code>{d.roleId}</Code>: {d.scope}
                        </Text>
                      ))}
                    </Stack>
                  </Alert>
                )}
              </Stack>
            )}
          </Card>
        </Grid.Col>

        {/* Snapshot Actions */}
        <Grid.Col span={12}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="md">
              <Text fw={600}>Snapshot Management</Text>
              <Group>
                <Button
                  leftSection={<IconCamera size={16} />}
                  onClick={handleGenerateSnapshot}
                  loading={snapshot.loading}
                >
                  Generate Snapshot
                </Button>
                {snapshot.snapshot && (
                  <Button variant="light" onClick={handleSetBaseline} loading={baseline.loading}>
                    Set as Baseline
                  </Button>
                )}
              </Group>
            </Group>

            {snapshot.snapshot && (
              <Paper withBorder p="sm" bg="gray.0">
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>
                      {snapshot.snapshot.snapshotId}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {snapshot.snapshot.roleCount} roles, {snapshot.snapshot.scopeRuleCount} scope
                      rules, {snapshot.snapshot.roleOverrideCount} overrides
                    </Text>
                  </div>
                  <Text size="xs" c="dimmed">
                    {new Date(snapshot.snapshot.generatedAt).toLocaleString()}
                  </Text>
                </Group>
              </Paper>
            )}
          </Card>
        </Grid.Col>

        {/* Login Simulation */}
        <Grid.Col span={12}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="md">
              <Text fw={600}>Login Simulation</Text>
              <Select
                placeholder="Select persona..."
                leftSection={<IconUser size={16} />}
                data={personas.personas.map((p) => ({
                  value: p.email,
                  label: `${p.email} (${p.role})`,
                }))}
                value={selectedPersona}
                onChange={setSelectedPersona}
                clearable
                w={300}
              />
            </Group>

            {simLoading && <Loader />}

            {simulation && (
              <Stack gap="md">
                <Paper withBorder p="md">
                  <Grid>
                    <Grid.Col span={3}>
                      <Text size="sm" c="dimmed">
                        Role
                      </Text>
                      <Code>{simulation.roleId}</Code>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Text size="sm" c="dimmed">
                        Tenant
                      </Text>
                      <Code>
                        {simulation.tenantSlug} (#{simulation.tenantNum})
                      </Code>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Text size="sm" c="dimmed">
                        Base Scopes
                      </Text>
                      <Text>{simulation.baseScopeCount}</Text>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Text size="sm" c="dimmed">
                        Total Scopes
                      </Text>
                      <Text fw={500}>
                        {simulation.totalScopeCount}
                        {simulation.overrideScopeCount > 0 && (
                          <Text span size="xs" c="dimmed">
                            {' '}
                            (+{simulation.overrideScopeCount} from override)
                          </Text>
                        )}
                      </Text>
                    </Grid.Col>
                  </Grid>
                </Paper>

                <Text fw={500}>Capability Checks</Text>
                <Progress
                  value={
                    (simulation.capabilityChecks.filter((c: CapabilityCheck) => c.passed).length /
                      simulation.capabilityChecks.length) *
                    100
                  }
                  color={simulation.allPassed ? 'green' : 'red'}
                  size="lg"
                />

                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Subgraph</Table.Th>
                      <Table.Th>Entity</Table.Th>
                      <Table.Th>Expected</Table.Th>
                      <Table.Th>Result</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {simulation.capabilityChecks.map((check: CapabilityCheck, i: number) => (
                      <Table.Tr key={i}>
                        <Table.Td>
                          <Code>{check.subgraph}</Code>
                        </Table.Td>
                        <Table.Td>{check.entity}</Table.Td>
                        <Table.Td>
                          {check.shouldHaveAccess ? (
                            <Badge color="blue" size="sm">
                              Access
                            </Badge>
                          ) : (
                            <Badge color="gray" size="sm">
                              Blocked
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {check.passed ? (
                            <ThemeIcon color="green" size="sm" variant="light">
                              <IconCheck size={14} />
                            </ThemeIcon>
                          ) : (
                            <ThemeIcon color="red" size="sm" variant="light">
                              <IconX size={14} />
                            </ThemeIcon>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>

                <Accordion>
                  <Accordion.Item value="scopes">
                    <Accordion.Control>
                      Effective Scopes ({simulation.effectiveScopes.length})
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Code block>{simulation.effectiveScopes.join('\n')}</Code>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Stack>
            )}
          </Card>
        </Grid.Col>
      </Grid>
    </Container>
  );
}

export default AuthObservabilityPage;
